import { PALETTE } from '../StageData';
import { ShadingPipeline } from '../../engine/ShadingPipeline';
import type { ShapeDrawFn } from './furnitureShapes';

export const musicDrawFns: Record<number, ShapeDrawFn> = {
  // STAGE 21: ロック・エレキギター
  21: (ctx, cx, cy, mode) => {
    const drawGuitarBody = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 30, cy - 30);
      ctx.bezierCurveTo(cx - 75, cy - 80, cx - 110, cy + 20, cx - 75, cy + 90);
      ctx.bezierCurveTo(cx - 45, cy + 140, cx + 45, cy + 140, cx + 75, cy + 90);
      ctx.bezierCurveTo(cx + 110, cy + 20, cx + 75, cy - 80, cx + 30, cy - 30);
      ctx.closePath();
    };
    const drawNeck = () => {
      ctx.beginPath();
      ctx.rect(cx - 14, cy - 170, 28, 140);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawNeck(); ctx.fill(); drawGuitarBody(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawNeck(); ctx.stroke();
        drawGuitarBody(); ctx.stroke();
        ctx.beginPath(); ctx.ellipse(cx + 15, cy + 20, 35, 45, 0.4, 0, Math.PI * 2); ctx.stroke();
        ctx.strokeRect(cx - 20, cy + 10, 40, 14);
        ctx.strokeRect(cx - 20, cy + 40, 40, 14);
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = PALETTE.wood; drawNeck(); ctx.fill();
      const grad = ctx.createRadialGradient(cx, cy + 40, 15, cx, cy + 40, 110);
      grad.addColorStop(0, PALETTE.gold);
      grad.addColorStop(0.65, PALETTE.crimson);
      grad.addColorStop(1, PALETTE.black);
      ctx.fillStyle = grad; drawGuitarBody(); ctx.fill();
      ctx.fillStyle = PALETTE.white;
      ctx.beginPath(); ctx.ellipse(cx + 15, cy + 20, 35, 45, 0.4, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 35, cy + 30, 25, 45, 0.55);
    }
  },

  // STAGE 22: レトロ木製ラジオ
  22: (ctx, cx, cy, mode) => {
    const drawRadio = () => {
      ctx.beginPath();
      ctx.roundRect(cx - 120, cy - 85, 240, 170, 22);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawRadio(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawRadio(); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx - 45, cy, 50, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx + 55, cy - 30, 20, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx + 55, cy + 30, 20, 0, Math.PI * 2); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 240, 170, PALETTE.cyan, 'lacquer');
      drawRadio(); ctx.fill();
      ctx.fillStyle = PALETTE.ivory;
      ctx.beginPath(); ctx.arc(cx - 45, cy, 50, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx + 55, cy - 30, 40, 40, PALETTE.gold, 'metallic');
      ctx.beginPath(); ctx.arc(cx + 55, cy - 30, 20, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx + 55, cy + 30, 20, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 60, cy - 55, 45, 15, 0.45);
    }
  },

  // STAGE 23: アコースティックギター
  23: (ctx, cx, cy, mode) => {
    const drawAcoustic = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy + 50, 95, 75, 0, 0, Math.PI * 2);
      ctx.ellipse(cx, cy - 35, 70, 55, 0, 0, Math.PI * 2);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawAcoustic(); ctx.fill();
      ctx.fillRect(cx - 12, cy - 180, 24, 150);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawAcoustic(); ctx.stroke();
        ctx.strokeRect(cx - 12, cy - 180, 24, 150);
        ctx.beginPath(); ctx.arc(cx, cy - 20, 28, 0, Math.PI * 2); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 100, 24, 150, PALETTE.darkWood, 'wood');
      ctx.fillRect(cx - 12, cy - 180, 24, 150);
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 10, 190, 150, PALETTE.wood, 'wood');
      drawAcoustic(); ctx.fill();
      ctx.fillStyle = PALETTE.black;
      ctx.beginPath(); ctx.arc(cx, cy - 20, 28, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 40, cy + 40, 25, 40, 0.4);
    }
  },

  // STAGE 24: ストラディバリ・バイオリン
  24: (ctx, cx, cy, mode) => {
    const drawViolin = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy + 45, 75, 65, 0, 0, Math.PI * 2);
      ctx.ellipse(cx, cy - 35, 60, 50, 0, 0, Math.PI * 2);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawViolin(); ctx.fill();
      ctx.fillRect(cx - 10, cy - 160, 20, 130);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawViolin(); ctx.stroke();
        ctx.strokeRect(cx - 10, cy - 160, 20, 130);
        ctx.beginPath();
        ctx.moveTo(cx - 30, cy - 10); ctx.bezierCurveTo(cx - 20, cy + 10, cx - 40, cy + 30, cx - 30, cy + 50);
        ctx.moveTo(cx + 30, cy - 10); ctx.bezierCurveTo(cx + 20, cy + 10, cx + 40, cy + 30, cx + 30, cy + 50);
        ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      const grad = ctx.createRadialGradient(cx, cy + 10, 15, cx, cy + 10, 95);
      grad.addColorStop(0, PALETTE.gold);
      grad.addColorStop(0.7, PALETTE.amber);
      grad.addColorStop(1, PALETTE.darkWood);
      ctx.fillStyle = grad; drawViolin(); ctx.fill();
      ctx.fillStyle = PALETTE.black; ctx.fillRect(cx - 10, cy - 160, 20, 130);
      ShadingPipeline.renderSpecularGlint(ctx, cx - 25, cy + 30, 20, 45, 0.6);
    }
  },

  // STAGE 25: 黄金のサックス
  25: (ctx, cx, cy, mode) => {
    const drawSax = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 40, cy - 140);
      ctx.lineTo(cx - 40, cy + 50);
      ctx.bezierCurveTo(cx - 40, cy + 130, cx + 60, cy + 130, cx + 60, cy + 40);
      ctx.bezierCurveTo(cx + 60, cy - 20, cx + 110, cy - 40, cx + 120, cy - 40);
      ctx.lineTo(cx + 70, cy + 40);
      ctx.bezierCurveTo(cx + 50, cy + 90, cx - 10, cy + 90, cx - 10, cy + 50);
      ctx.lineTo(cx - 10, cy - 140);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawSax(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawSax(); ctx.stroke();
        for (let i = 0; i < 5; i++) {
          ctx.beginPath(); ctx.arc(cx - 25, cy - 80 + i * 26, 8, 0, Math.PI * 2); ctx.stroke();
        }
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx + 30, cy, 160, 250, PALETTE.gold, 'metallic');
      drawSax(); ctx.fill();
      ctx.fillStyle = PALETTE.white;
      for (let i = 0; i < 5; i++) {
        ctx.beginPath(); ctx.arc(cx - 25, cy - 80 + i * 26, 8, 0, Math.PI * 2); ctx.fill();
      }
      ShadingPipeline.renderSpecularGlint(ctx, cx + 80, cy - 10, 22, 35, 0.7);
    }
  },

  // STAGE 26: ロックンロール・ドラム
  26: (ctx, cx, cy, mode) => {
    const drawSnare = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy - 50, 110, 36, 0, 0, Math.PI * 2);
      ctx.rect(cx - 110, cy - 50, 220, 100);
      ctx.ellipse(cx, cy + 50, 110, 36, 0, 0, Math.PI * 2);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawSnare(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawSnare(); ctx.stroke();
        ctx.beginPath(); ctx.ellipse(cx, cy - 50, 110, 36, 0, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath();
        for (let i = -3; i <= 3; i++) {
          ctx.moveTo(cx + i * 30, cy - 40); ctx.lineTo(cx + i * 30, cy + 45);
        }
        ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 220, 100, PALETTE.crimson, 'lacquer');
      ctx.fillRect(cx - 110, cy - 50, 220, 100);
      ctx.fillStyle = PALETTE.white;
      ctx.beginPath(); ctx.ellipse(cx, cy - 50, 110, 36, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = PALETTE.silver;
      ctx.beginPath(); ctx.ellipse(cx, cy + 50, 110, 36, 0, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 40, cy - 60, 45, 12, 0.5);
    }
  },

  // STAGE 27: グランドピアノ
  27: (ctx, cx, cy, mode) => {
    const drawPianoBody = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 120, cy + 60);
      ctx.lineTo(cx - 120, cy - 80);
      ctx.bezierCurveTo(cx - 60, cy - 150, cx + 80, cy - 130, cx + 110, cy - 40);
      ctx.bezierCurveTo(cx + 120, cy + 20, cx + 90, cy + 60, cx + 90, cy + 60);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawPianoBody(); ctx.fill();
      ctx.fillRect(cx - 110, cy + 60, 190, 40);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawPianoBody(); ctx.stroke();
        ctx.strokeRect(cx - 110, cy + 60, 190, 40);
        ctx.beginPath();
        for (let i = 0; i < 14; i++) {
          ctx.moveTo(cx - 110 + i * 13.5, cy + 60); ctx.lineTo(cx - 110 + i * 13.5, cy + 100);
        }
        ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 30, 230, 180, PALETTE.black, 'lacquer');
      drawPianoBody(); ctx.fill();
      ctx.fillStyle = PALETTE.white; ctx.fillRect(cx - 110, cy + 60, 190, 40);
      ctx.fillStyle = PALETTE.black;
      for (let i = 0; i < 10; i++) {
        if (i % 7 !== 2 && i % 7 !== 6) ctx.fillRect(cx - 105 + i * 18, cy + 60, 11, 24);
      }
      ShadingPipeline.renderSpecularGlint(ctx, cx - 20, cy - 75, 55, 20, 0.65);
    }
  },

  // STAGE 28: アナログ・ターンテーブル
  28: (ctx, cx, cy, mode) => {
    const drawPlinth = () => {
      ctx.beginPath();
      ctx.roundRect(cx - 120, cy - 90, 240, 180, 14);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawPlinth(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawPlinth(); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx - 25, cy, 65, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx - 25, cy, 22, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx + 70, cy - 60); ctx.lineTo(cx + 50, cy + 40);
        ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 240, 180, PALETTE.dark, 'wood');
      drawPlinth(); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx - 25, cy, 130, 130, PALETTE.black, 'lacquer');
      ctx.beginPath(); ctx.arc(cx - 25, cy, 65, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = PALETTE.crimson;
      ctx.beginPath(); ctx.arc(cx - 25, cy, 22, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 45, cy - 25, 30, 30, 0.4);
    }
  },

  // STAGE 29: 真鍮のトランペット
  29: (ctx, cx, cy, mode) => {
    const drawTrumpet = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 130, cy);
      ctx.lineTo(cx + 70, cy);
      ctx.bezierCurveTo(cx + 100, cy - 10, cx + 130, cy - 50, cx + 130, cy - 50);
      ctx.lineTo(cx + 130, cy + 50);
      ctx.bezierCurveTo(cx + 100, cy + 10, cx + 70, cy, cx + 70, cy);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawTrumpet(); ctx.fill();
      ctx.fillRect(cx - 20, cy - 40, 45, 40);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawTrumpet(); ctx.stroke();
        ctx.strokeRect(cx - 20, cy - 40, 45, 40);
        for (let i = 0; i < 3; i++) {
          ctx.strokeRect(cx - 16 + i * 14, cy - 55, 10, 15);
        }
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 260, 100, PALETTE.gold, 'metallic');
      drawTrumpet(); ctx.fill();
      ctx.fillStyle = PALETTE.brass; ctx.fillRect(cx - 20, cy - 40, 45, 40);
      ShadingPipeline.renderSpecularGlint(ctx, cx + 85, cy - 15, 25, 35, 0.65);
    }
  },

  // STAGE 30: ビンテージ・スタジオマイク
  30: (ctx, cx, cy, mode) => {
    const drawMic = () => {
      ctx.beginPath();
      ctx.roundRect(cx - 50, cy - 100, 100, 150, 24);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawMic(); ctx.fill();
      ctx.fillRect(cx - 10, cy + 50, 20, 70);
      ctx.beginPath(); ctx.ellipse(cx, cy + 120, 60, 18, 0, 0, Math.PI * 2); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawMic(); ctx.stroke();
        ctx.strokeRect(cx - 10, cy + 50, 20, 70);
        ctx.beginPath(); ctx.ellipse(cx, cy + 120, 60, 18, 0, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          ctx.moveTo(cx - 50, cy - 80 + i * 20); ctx.lineTo(cx + 50, cy - 80 + i * 20);
        }
        ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 25, 100, 150, PALETTE.silver, 'metallic');
      drawMic(); ctx.fill();
      ctx.fillStyle = PALETTE.gold; ctx.fillRect(cx - 45, cy - 20, 90, 8);
      ctx.fillStyle = PALETTE.dark;
      ctx.fillRect(cx - 10, cy + 50, 20, 70);
      ctx.beginPath(); ctx.ellipse(cx, cy + 120, 60, 18, 0, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 20, cy - 60, 25, 40, 0.6);
    }
  }
};
